import { unwrapResult } from "@reduxjs/toolkit";
import { formatISO, getHours, getMinutes, set } from "date-fns";
import { first, omit, pick } from "lodash";
import { saveOrderRequest } from "store/requests/library.requests";
import { calculateOrderPriceRequest, createNewOrderRequest } from "store/requests/newOrder.requests";
import { checkoutNewOrderRequest, paymentRequest } from "store/requests/payment.request";
import { format } from "utility/date-fns";
import i18n from "utility/i18next";
import { fromPromise } from "xstate";
import { REPEAT_ORDER_TYPES } from "../../constants";
import store from "../../store/index";

const { dispatch } = store;

export const calculateOrderPriceActor = fromPromise(async ({ input: { event } }) => {
  const price = await dispatch(
    calculateOrderPriceRequest(
      pick(event.values, ["startDate", "endDate", "pick_up", "drops", "isExpress", "repeat_settings"])
    )
  ).then(unwrapResult);
  return { order: event.values, price, ...event };
});

export const preCheckoutActor = fromPromise(async ({ input: { context } }) => {
  const { pick_up, startDate } = context.order;
  const start = new Date(startDate);

  const lines = context.price.map(({ date, ...price }) => {
    const { reference } = context.references.find((currRef) => currRef.date === date);
    return {
      reference,
      price,
      description: `${format(
        formatISO(set(new Date(date), { hours: getHours(start), minutes: getMinutes(start) })),
        "yyyy-MM-dd HH:mm"
      )}/${pick_up.location}/${reference}`,
    };
  });

  const res = await dispatch(checkoutNewOrderRequest({ lines, intentId: context.checkout.id })).then(unwrapResult);
  return { ...omit(res.data, ["client_secret"]), secret: res.data.client_secret };
});

export const submitBookingActor = fromPromise(async ({ input: { context, event } }) => {
  const { order, checkout } = context;

  const res = await dispatch(
    createNewOrderRequest({
      ...pick(context, ["order", "price", "references"]),
      customer: omit(event.values, ["save"]),
      intentId: checkout.id,
    })
  ).then(unwrapResult);

  let references = [];

  if (order.repeat_settings.type !== REPEAT_ORDER_TYPES.EXACT_DATES) {
    references = [{ id: res.data.id, reference: res.data.reference }];
  } else {
    references = res.data.map(({ id, reference, delivery_date }) => ({
      id,
      reference,
      date: delivery_date,
    }));
  }

  return { references, ...pick(event.values, ["utils"]) };
});

export const checkoutActor = fromPromise(async ({ input: { context, event } }) => {
  const { defaultCard, stripe, elements, expressCheckout } = event.values.utils;

  await dispatch(
    paymentRequest({
      defaultCard,
      stripe,
      elements,
      expressCheckout,
      checkout: context.checkout,
      customer: context.customer,
    })
  ).then(unwrapResult);

  return { references: context.references, ...pick(event.values, ["references"]) };
});

export const saveBookingActor = fromPromise(async ({ input: { context } }) => {
  const { id, reference } = first(context.references);
  await dispatch(saveOrderRequest({ id, label: i18n.t("dashboard.order.copyOfOrderLabel", { reference }) })).then(
    unwrapResult
  );
});
